import axios from "axios";
import { useQuery, useMutation } from "@tanstack/react-query";
import { API_BASE_RELATIVE_PATH, API_BASE_PATH, API_BACKEND_URL } from "../Config/Config";

// Helper function to get user details
const getUserDetails = () => {
  const userDetails = localStorage.getItem("userDetail");
  if (!userDetails) throw new Error("No user details found in localStorage");
  return JSON.parse(userDetails);
};

// ==================== ENVIRONMENTS ====================

// Get all environments
const fetchEnvironments = async () => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/environment?companyid=${user.companyid}`,
  );

  return response.data;
};

export const useGetEnvironmentsQuery = (options = {}) => {
  return useQuery({
    queryKey: ["environments"],
    queryFn: fetchEnvironments,
    ...options,
  });
};

// Create new environment
const createEnvironment = async (environmentData) => {
  const user = getUserDetails();

  const response = await axios.post(
    `${API_BACKEND_URL}/environment/add?companyid=${user.companyid}`,
    environmentData,
  );

  return response.data;
};

export const useCreateEnvironmentMutation = (options = {}) => {
  return useMutation({
    mutationFn: createEnvironment,
    ...options,
  });
};

const deleteEnvironment = async (environmentId) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/environment/deleteEnvironment?companyid=${user.companyid}&environmentId=${environmentId}`,
  );

  return response.data;
};
export const useDeleteEnvironment = (options = {}) => {
  return useMutation({
    mutationFn: deleteEnvironment,
    ...options,
  });
};

// ==================== ENVIRONMENT VARIABLES ====================

// Get variables for a specific environment
const fetchEnvironmentVariables = async (environmentId) => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/environment/getEnvironmentVariables?companyid=${user.companyid}&environmentId=${environmentId}`,
  );

  return response.data;
};

export const useGetEnvironmentVariablesQuery = (environmentId, options = {}) => {
  return useQuery({
    queryKey: ["environmentVariables", environmentId],
    queryFn: () => fetchEnvironmentVariables(environmentId),
    enabled: !!environmentId && options.enabled !== false,
    ...options,
  });
};

// Add new variable
const addEnvironmentVariable = async ({ variableData }) => {
  const user = getUserDetails();

  const response = await axios.post(
    `${API_BACKEND_URL}/environment/addVariable?companyid=${user.companyid}`,
    variableData,
  );

  return response.data;
};

export const useAddEnvironmentVariableMutation = (options = {}) => {
  return useMutation({
    mutationFn: addEnvironmentVariable,
    ...options,
  });
};

// Update existing variable
const updateEnvironmentVariable = async ({ variableId, variableData }) => {
  const response = await axios.put(
    `${API_BACKEND_URL}/environment/updateVariable/${variableId}`,
    variableData,
  );

  return response.data;
};

export const useUpdateEnvironmentVariableMutation = (options = {}) => {
  return useMutation({
    mutationFn: updateEnvironmentVariable,
    ...options,
  });
};

// Delete variable
const deleteEnvironmentVariable = async ({ variableId }) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/environment/deleteEnvironmentVariable?companyid=${user.companyid}&variableId=${variableId}`,
  );

  return response.data;
};

export const useDeleteEnvironmentVariableMutation = (options = {}) => {
  return useMutation({
    mutationFn: deleteEnvironmentVariable,
    ...options,
  });
};

// Rename variable (update key name)
const renameEnvironmentVariable = async ({ environmentId, variableId, newKey }) => {
  const user = getUserDetails();

  const response = await axios.patch(
    `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=renameEnvironmentVariable&companyid=${user.companyid}&environmentId=${environmentId}&variableId=${variableId}`,
    { key: newKey },
  );

  return response.data;
};

export const useRenameEnvironmentVariableMutation = (options = {}) => {
  return useMutation({
    mutationFn: renameEnvironmentVariable,
    ...options,
  });
};

//set environment for a suite
const setEnvForSuite = async ({ testSuiteId, environmentId }) => {
  const user = getUserDetails();
  const response = await axios.put(
    `${API_BACKEND_URL}/testsuite/setEnvironmentToTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}&environmentId=${environmentId}`,
  );
  return response.data;
};

export const useSetEnvForSuite = (options = {}) => {
  return useMutation({
    mutationFn: setEnvForSuite,
    ...options,
  });
};

//delete/remove environment for a suite

const removeEnvForSuite = async ({ testSuiteId }) => {
  const user = getUserDetails();
  const response = await axios.put(
    `${API_BACKEND_URL}/testsuite/removeEnvironmentForTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}`,
  );
  return response.data;
};

export const useRemoveEnvForSuite = (options = {}) => {
  return useMutation({
    mutationFn: removeEnvForSuite,
    ...options,
  });
};

// ============ ADD ENV TO  Test Case / Endpoint

const addVariabletoTcEnd = async ({ endpointId = -1, testCaseId = -1, variables }) => {
  const user = getUserDetails();

  // const variablePayload = {
  //   [variable.key]: variable.value,
  // };

  const response = await axios.put(
    `${API_BACKEND_URL}/addVariablesToTCOrEndpoint?companyId=${user.companyid}&endpointId=${endpointId}&testCaseId=${testCaseId}`,
    variables,
  );
  return response.data;
};

export const useAddVariablesTcEnd = (options = {}) => {
  return useMutation({
    mutationFn: addVariabletoTcEnd,
    ...options,
  });
};

// ==================== GLOBAL VARIABLES ====================

// Add  global variable
const addGlobalVariable = async ({ variableData }) => {
  const user = getUserDetails();

  const response = await axios.post(
    `${API_BACKEND_URL}/globalVariable/addGlobalVariable?companyId=${user.companyid}`,
    variableData,
  );

  return response.data;
};

export const useAddGlobalVariable = (options = {}) => {
  return useMutation({
    mutationFn: addGlobalVariable,
    ...options,
  });
};

//Get global variables
const getGlobalVariables = async () => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/globalVariable?companyId=${user.companyid}`,
  );

  return response.data;
};

export const useGetGlobalVariables = (options = {}) => {
  return useQuery({
    queryKey: ["globalVariables"],
    queryFn: getGlobalVariables,
    ...options,
  });
};

const deleteGlobalVariable = async ({ variableId }) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/globalVariable/deleteGlobalVariable?companyId=${user.companyid}&id=${variableId}`,
  );

  return response.data;
};

export const useDeleteGlobalVariable = (options = {}) => {
  return useMutation({
    mutationFn: deleteGlobalVariable,
    ...options,
  });
};

// Default exports
export default {
  useGetEnvironmentsQuery,
  useCreateEnvironmentMutation,
  useGetEnvironmentVariablesQuery,
  useAddEnvironmentVariableMutation,
  useUpdateEnvironmentVariableMutation,
  useDeleteEnvironmentVariableMutation,
  useRenameEnvironmentVariableMutation,
  useDeleteEnvironment,
};
